import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ui/error-boundary';
import NetworkStatusIndicator from './components/ui/network-status';
import CookieConsent from './components/CookieConsent';
import LandingPage from './pages/LandingPage'; // Import directly to avoid loading screen
import LoginPage from './pages/LoginPage'; // Import directly for fast navigation
import ResetPasswordPage from './pages/ResetPasswordPage';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Lazy load authenticated pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TripDetail = lazy(() => import('./pages/TripDetail'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const SharedTripView = lazy(() => import('./pages/SharedTripView'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// Loading fallback component
const PageLoader = () => (
  <div 
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: 'var(--color-background)' }}
  >
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      <div 
        className="text-lg"
        style={{ color: 'var(--color-on-surface)' }}
      >
        Loading...
      </div>
    </div>
  </div>
);

// Component to handle redirect with query params preserved
function AuthenticatedRedirect({ to }) {
  const location = useLocation();
  // Preserve query params when redirecting
  const redirectPath = location.search ? `${to}${location.search}` : to;
  return <Navigate to={redirectPath} replace />;
}

function AppRoutesInner() {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={isAuthenticated ? <AuthenticatedRedirect to="/dashboard" /> : <LandingPage />} />
        <Route path="/login" element={isAuthenticated ? <AuthenticatedRedirect to="/dashboard" /> : <LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/shared/:shareToken" element={<SharedTripView />} />
        <Route
          path="/dashboard"
          element={isAuthenticated ? (
            <ErrorBoundary>
              <Dashboard />
            </ErrorBoundary>
          ) : <Navigate to="/" />}
        />
        <Route
          path="/trip/:tripId"
          element={isAuthenticated ? (
            <ErrorBoundary>
              <TripDetail />
            </ErrorBoundary>
          ) : <Navigate to="/" />}
        />
        <Route
          path="/subscription"
          element={isAuthenticated ? (
            <ErrorBoundary>
              <SubscriptionPage />
            </ErrorBoundary>
          ) : <Navigate to="/" />}
        />
        <Route
          path="/subscription/success"
          element={isAuthenticated ? (
            <ErrorBoundary>
              <SubscriptionSuccess />
            </ErrorBoundary>
          ) : <Navigate to="/" />}
        />
      </Routes>
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          {GOOGLE_MAPS_API_KEY ? (
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
              <AppRoutesInner />
            </APIProvider>
          ) : (
            <AppRoutesInner />
          )}
        </ErrorBoundary>
      </BrowserRouter>
      <NetworkStatusIndicator position="bottom" />
      <Toaster position="top-right" />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
